import { ID, Permission, Query, Role } from "appwrite";

import {
  APPWRITE_DATABASE_ID,
  PROJECTS_TABLE_ID,
  PROJECT_DEVICES_TABLE_ID,
  PROJECT_MEMBERS_TABLE_ID,
  PROJECT_PAGES_TABLE_ID,
  PROJECT_PRESENCE_TABLE_ID,
  tablesDB
} from "src/handlers/Appwrite";
import { listAllRows } from "src/handlers/Appwrite/utils";
import { repairProjectPermissions } from "src/handlers/ProjectSync";
import { sanitizeId } from "src/handlers/utils";

import { clearActiveProject, getActiveProjectId, setActiveProject } from "./activeSession";
import {
  getDefaultProjectSnapshot,
  normalizeProjectSnapshot,
  safeParseJson
} from "./schema";

// ---------------------------------------------------------------------------
// Helpers (private to this module)
// ---------------------------------------------------------------------------

function parseJson(value, fallback) {
  return safeParseJson(value, fallback);
}

function makeRowId(...parts) {
  return sanitizeId(parts.join("_"));
}

function getCurrentUser() {
  return parseJson(localStorage.getItem("userinfo"), null);
}

function getUserId() {
  return getCurrentUser()?.$id || "";
}

// User-owned project rows get the project owner's full ACL set. Shared
// projects layer additional Team-derived permissions on top via the
// project-sharing function; this helper only covers the base case.
function getProjectPermissions(userId) {
  return [
    Permission.read(Role.user(userId)),
    Permission.update(Role.user(userId)),
    Permission.delete(Role.user(userId))
  ];
}

// ---------------------------------------------------------------------------
// Local snapshot mirror (the localStorage keys the editors read from)
// ---------------------------------------------------------------------------

function getLocalProjectSnapshot() {
  const defaults = getDefaultProjectSnapshot();

  return normalizeProjectSnapshot({
    router_data: parseJson(localStorage.getItem("router_data"), defaults.router_data),
    router_final: parseJson(localStorage.getItem("router_final"), defaults.router_final),
    switch_data: parseJson(localStorage.getItem("switch_data"), defaults.switch_data),
    switch_final: parseJson(localStorage.getItem("switch_final"), defaults.switch_final),
    vlan_data: parseJson(localStorage.getItem("vlan_data"), defaults.vlan_data)
  });
}

function applyProjectSnapshot(snapshot) {
  const nextSnapshot = normalizeProjectSnapshot(snapshot);

  localStorage.setItem("router_data", JSON.stringify(nextSnapshot.router_data));
  localStorage.setItem("router_final", JSON.stringify(nextSnapshot.router_final));
  localStorage.setItem("switch_data", JSON.stringify(nextSnapshot.switch_data));
  localStorage.setItem("switch_final", JSON.stringify(nextSnapshot.switch_final));
  localStorage.setItem("vlan_data", JSON.stringify(nextSnapshot.vlan_data));
}

// ---------------------------------------------------------------------------
// Lifecycle: touch (last-opened bump), list helpers
// ---------------------------------------------------------------------------

async function touchProject(project) {
  const projectId = project?.$id || project?.projectId || "";
  const currentUserId = getUserId();

  if (!projectId || !currentUserId) return;
  if (project?.ownerUserId && project.ownerUserId !== currentUserId) return;

  try {
    await tablesDB.updateRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECTS_TABLE_ID,
      rowId: projectId,
      data: {
        updatedAtMs: Date.now(),
        lastOpenedAtMs: Date.now()
      }
    });
  } catch (error) {
    if (error?.code === 401 || error?.code === 403) {
      return;
    }
    console.error("Failed to update project timestamp", error);
  }
}

async function listProjectRows(tableId, projectId, queries = []) {
  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId,
    queries: [Query.equal("projectId", [projectId]), ...queries]
  });
}

// ---------------------------------------------------------------------------
// Snapshot persistence (router/switch device rows + vlan page row)
// ---------------------------------------------------------------------------

async function saveDeviceRows(projectId, userId, deviceType, devices) {
  const existingRows = await listProjectRows(PROJECT_DEVICES_TABLE_ID, projectId, [
    Query.equal("deviceType", [deviceType])
  ]);

  const rowsByDeviceId = new Map(
    existingRows.map((row) => [Number(row.deviceId), row])
  );

  const nextIds = new Set();

  await Promise.all(
    (devices || []).map(async (device, deviceId) => {
      nextIds.add(deviceId);
      const rowId = makeRowId(projectId, deviceType, deviceId);
      const payload = {
        projectId,
        deviceType,
        deviceId,
        label: device?.initial?.[0]?.hostname || `${deviceType}-${deviceId + 1}`,
        dataJson: JSON.stringify(device ?? null),
        updatedAtMs: Date.now()
      };

      if (rowsByDeviceId.has(deviceId)) {
        await tablesDB.updateRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECT_DEVICES_TABLE_ID,
          rowId,
          data: payload
        });
        return;
      }

      await tablesDB.createRow({
        databaseId: APPWRITE_DATABASE_ID,
        tableId: PROJECT_DEVICES_TABLE_ID,
        rowId,
        data: payload,
        permissions: getProjectPermissions(userId)
      });
    })
  );

  await Promise.all(
    existingRows
      .filter((row) => !nextIds.has(Number(row.deviceId)))
      .map((row) =>
        tablesDB.deleteRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECT_DEVICES_TABLE_ID,
          rowId: row.$id
        })
      )
  );
}

async function savePageRow(projectId, userId, pageKey, data) {
  const rowId = makeRowId(projectId, pageKey);

  try {
    await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId
    });

    await tablesDB.updateRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId,
      data: {
        projectId,
        pageKey,
        dataJson: JSON.stringify(data ?? null),
        updatedAtMs: Date.now()
      }
    });
  } catch (error) {
    if (error?.code !== 404) throw error;

    await tablesDB.createRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_PAGES_TABLE_ID,
      rowId,
      data: {
        projectId,
        pageKey,
        dataJson: JSON.stringify(data ?? null),
        updatedAtMs: Date.now()
      },
      permissions: getProjectPermissions(userId)
    });
  }
}

export async function saveProjectSnapshot(projectId, snapshot, userIdOverride = "") {
  const userId = userIdOverride || getUserId();
  if (!projectId || !userId) return;

  await saveDeviceRows(projectId, userId, "router", snapshot?.router_data || []);
  await saveDeviceRows(projectId, userId, "switch", snapshot?.switch_data || []);
  await savePageRow(projectId, userId, "vlan", snapshot?.vlan_data || []);
  await touchProject(projectId);
}

export async function loadProjectSnapshot(projectId) {
  if (!projectId) return getDefaultProjectSnapshot();

  const [routerRows, switchRows, vlanRows] = await Promise.all([
    listProjectRows(PROJECT_DEVICES_TABLE_ID, projectId, [
      Query.equal("deviceType", ["router"]),
      Query.orderAsc("deviceId")
    ]),
    listProjectRows(PROJECT_DEVICES_TABLE_ID, projectId, [
      Query.equal("deviceType", ["switch"]),
      Query.orderAsc("deviceId")
    ]),
    listProjectRows(PROJECT_PAGES_TABLE_ID, projectId, [
      Query.equal("pageKey", ["vlan"])
    ])
  ]);

  return normalizeProjectSnapshot({
    router_data: routerRows.map((row) => parseJson(row.dataJson, null)).filter(Boolean),
    switch_data: switchRows.map((row) => parseJson(row.dataJson, null)).filter(Boolean),
    vlan_data: parseJson(vlanRows[0]?.dataJson, getDefaultProjectSnapshot().vlan_data)
  });
}

// ---------------------------------------------------------------------------
// Project CRUD
// ---------------------------------------------------------------------------

/** @returns {Promise<import("src/handlers/types").ProjectRow[]>} */
export async function listProjects() {
  const userId = getUserId();
  if (!userId) return [];

  const memberships = await listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_MEMBERS_TABLE_ID,
    queries: [Query.equal("userId", [userId]), Query.orderDesc("joinedAtMs")]
  });
  const projectIds = [...new Set(memberships.map((row) => row.projectId).filter(Boolean))];

  const projects = await Promise.all(
    projectIds.map(async (projectId) => {
      try {
        return await tablesDB.getRow({
          databaseId: APPWRITE_DATABASE_ID,
          tableId: PROJECTS_TABLE_ID,
          rowId: projectId
        });
      } catch {
        return null;
      }
    })
  );

  return projects
    .filter((project) => project && !project.archived)
    .sort((a, b) => Number(b.updatedAtMs || 0) - Number(a.updatedAtMs || 0));
}

/**
 * @param {string} projectId
 * @returns {Promise<import("src/handlers/types").ProjectRow | null>}
 */
export async function getProjectById(projectId) {
  if (!projectId) return null;

  try {
    return await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECTS_TABLE_ID,
      rowId: projectId
    });
  } catch {
    return null;
  }
}

/**
 * @param {string} projectId
 * @param {string} [userIdOverride]
 * @returns {Promise<import("src/handlers/types").ProjectMemberRow | null>}
 */
export async function getProjectMembership(projectId, userIdOverride = "") {
  const userId = userIdOverride || getUserId();
  if (!projectId || !userId) return null;

  try {
    return await tablesDB.getRow({
      databaseId: APPWRITE_DATABASE_ID,
      tableId: PROJECT_MEMBERS_TABLE_ID,
      rowId: makeRowId(projectId, userId)
    });
  } catch {
    return null;
  }
}

/** @returns {Promise<import("src/handlers/types").ProjectMemberRow[]>} */
export async function listProjectMemberships() {
  const userId = getUserId();
  if (!userId) return [];

  return listAllRows(tablesDB, {
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_MEMBERS_TABLE_ID,
    queries: [Query.equal("userId", [userId]), Query.orderDesc("joinedAtMs")]
  });
}

/**
 * @param {{ name?: string, snapshot?: any, setActive?: boolean }} [opts]
 * @returns {Promise<import("src/handlers/types").ProjectRow>}
 */
export async function createProject({
  name = "Untitled project",
  snapshot = getDefaultProjectSnapshot(),
  setActive = true
} = {}) {
  const userId = getUserId();
  if (!userId) {
    throw new Error("You must be signed in to create projects.");
  }

  const nextSnapshot = normalizeProjectSnapshot(snapshot);
  const now = Date.now();
  const project = await tablesDB.createRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECTS_TABLE_ID,
    rowId: ID.unique(),
    data: {
      ownerUserId: userId,
      name,
      shareEnabled: false,
      defaultAccess: "private",
      archived: false,
      createdAtMs: now,
      updatedAtMs: now,
      lastOpenedAtMs: now
    },
    permissions: getProjectPermissions(userId)
  });

  await tablesDB.createRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECT_MEMBERS_TABLE_ID,
    rowId: makeRowId(project.$id, userId),
    data: {
      projectId: project.$id,
      userId,
      role: "owner",
      joinedAtMs: now,
      invitedByUserId: userId
    },
    permissions: getProjectPermissions(userId)
  });

  await saveProjectSnapshot(project.$id, nextSnapshot, userId);

  if (setActive) {
    setActiveProject(project);
    applyProjectSnapshot(nextSnapshot);
  }

  return project;
}

export async function renameProject(projectId, name) {
  if (!projectId || !name?.trim()) return null;

  const project = await tablesDB.updateRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECTS_TABLE_ID,
    rowId: projectId,
    data: {
      name: name.trim(),
      updatedAtMs: Date.now()
    }
  });

  if (projectId === getActiveProjectId()) {
    setActiveProject(project);
  }

  return project;
}

export async function duplicateProject(
  projectId,
  { name = "", setActive = true } = {}
) {
  if (!projectId) return null;

  const sourceProject = await getProjectById(projectId);
  if (!sourceProject) {
    throw new Error("Project not found.");
  }

  const snapshot = await loadProjectSnapshot(projectId);

  return createProject({
    name: name.trim() || `${sourceProject.name} copy`,
    snapshot,
    setActive
  });
}

async function deleteRowsByProject(tableId, projectId) {
  await Promise.all(
    (await listProjectRows(tableId, projectId)).map((row) =>
      tablesDB.deleteRow({
        databaseId: APPWRITE_DATABASE_ID,
        tableId,
        rowId: row.$id
      })
    )
  );
}

export async function deleteProject(projectId) {
  if (!projectId) return;

  await Promise.all([
    deleteRowsByProject(PROJECT_DEVICES_TABLE_ID, projectId),
    deleteRowsByProject(PROJECT_PAGES_TABLE_ID, projectId),
    deleteRowsByProject(PROJECT_PRESENCE_TABLE_ID, projectId),
    deleteRowsByProject(PROJECT_MEMBERS_TABLE_ID, projectId)
  ]);

  await tablesDB.deleteRow({
    databaseId: APPWRITE_DATABASE_ID,
    tableId: PROJECTS_TABLE_ID,
    rowId: projectId
  });

  if (projectId === getActiveProjectId()) {
    clearActiveProject();
  }
}

export async function syncProjectCollaboratorPermissions(projectId) {
  if (!projectId) return;

  try {
    await repairProjectPermissions({ projectId });
  } catch (error) {
    console.warn("Could not sync collaborator permissions from client", error);
  }
}

// ---------------------------------------------------------------------------
// Switching + first-load lifecycle
// ---------------------------------------------------------------------------

export async function switchProject(project) {
  if (!project?.$id) return null;

  const snapshot = await loadProjectSnapshot(project.$id);
  applyProjectSnapshot(snapshot);
  setActiveProject(project);
  await touchProject(project);
  window.dispatchEvent(
    new CustomEvent("projectSwitched", { detail: { projectId: project.$id } })
  );
  return snapshot;
}

export async function ensureActiveProjectSession() {
  const userId = getUserId();
  if (!userId) return null;

  const projects = await listProjects();
  const activeProjectId = getActiveProjectId();
  const activeProject = projects.find((project) => project.$id === activeProjectId);

  if (activeProject) {
    await switchProject(activeProject);
    return activeProject;
  }

  if (projects.length) {
    await switchProject(projects[0]);
    return projects[0];
  }

  const seededSnapshot = getLocalProjectSnapshot();
  return createProject({
    name: "My first project",
    snapshot: seededSnapshot,
    setActive: true
  });
}
